import { DISCLOSURE_BUTTON_CLASS } from './Constants';
import {
    findButtonForSubmenu,
    getJQuery,
    getSmartMenusInstance,
    isShowcaseWrapper,
    SHOWCASE_DISMISSED_CLASS,
} from './Utils';

let $: JQueryStatic;

// Guards against duplicate document-level Escape listeners if
// setupArrowKeyNavigation is ever called more than once (re-init, HMR, etc.).
let documentEscapeBound = false;

// =============================================================================
// Helpers
// =============================================================================

/**
 * Safely set focus on a jQuery element at the given index.
 */
const focusAt = ($collection: JQuery, index: number): void => {
    const el = $collection[index] as HTMLElement | undefined;
    el?.focus();
};

// =============================================================================
// Focus Context Detection
// =============================================================================

/**
 * Check whether an element is a top-level nav item (link or disclosure button)
 * that is a direct child of a `li.top-nav`.
 */
const isTopLevel = (el: HTMLElement): boolean => {
    return $(el).parent('li.top-nav').length > 0;
};

/**
 * Check whether an element is inside a dropdown menu.
 */
const isInDropdown = (el: HTMLElement): boolean => {
    return $(el).closest('ul.dropdown-menu').length > 0;
};

// =============================================================================
// Focusable Item Collection
// =============================================================================

/**
 * Get all focusable top-level items (links and disclosure buttons) in DOM order.
 * These are the direct `<a>` and `<button>` children of each `li.top-nav`.
 */
const getTopLevelItems = ($menu: JQuery): JQuery => {
    return $menu.find('> li.top-nav').children(`a, .${DISCLOSURE_BUTTON_CLASS}`);
};

/**
 * Get all focusable items in the immediate dropdown (not nested sub-dropdowns).
 *
 * Uses `.find()` to handle both standard dropdowns (`<li> > <a>`) and showcase
 * menus where links are nested inside wrapper `<div>` elements. Filters out
 * items that belong to nested sub-dropdowns by checking each element's closest
 * `ul.dropdown-menu` ancestor matches the target dropdown.
 */
const getDropdownItems = ($dropdown: JQuery): JQuery => {
    return $dropdown.find(`a, .${DISCLOSURE_BUTTON_CLASS}`).filter(function () {
        return $(this).closest('ul.dropdown-menu')[0] === $dropdown[0];
    });
};

// =============================================================================
// Arrow Key Navigation
// =============================================================================

/** Keys handled by arrow key navigation */
const ARROW_NAV_KEYS = new Set(['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'Escape']);

/**
 * If the element is an expanded disclosure button, move focus into
 * the first item of its controlled dropdown. Returns true if focus moved.
 */
const tryEnterExpandedDropdown = ($el: JQuery): boolean => {
    if (!$el.is(`.${DISCLOSURE_BUTTON_CLASS}`)) {
        return false;
    }
    if ($el.attr('aria-expanded') !== 'true') {
        return false;
    }
    const controlsId = $el.attr('aria-controls');
    if (!controlsId) {
        return false;
    }
    const $dropdown = $(`#${controlsId}`);
    const $items = getDropdownItems($dropdown);
    if (!$items.length) {
        return false;
    }
    focusAt($items, 0);
    return true;
};

/**
 * Handle Down Arrow / Right Arrow navigation.
 *
 * - Top-level item with collapsed dropdown: move to next top-level item.
 * - Top-level disclosure button with expanded dropdown: move to first dropdown link.
 * - Inside dropdown: move to next item in the dropdown.
 */
const handleArrowForward = (el: HTMLElement, $menu: JQuery): void => {
    if (isTopLevel(el)) {
        const $el = $(el);
        if (tryEnterExpandedDropdown($el)) {
            return;
        }

        const $topItems = getTopLevelItems($menu);
        const idx = $topItems.index(el);
        if (idx >= 0 && idx < $topItems.length - 1) {
            focusAt($topItems, idx + 1);
        }
        return;
    }

    if (isInDropdown(el)) {
        const $el = $(el);
        if (tryEnterExpandedDropdown($el)) {
            return;
        }

        const $dropdown = $el.closest('ul.dropdown-menu');
        const $items = getDropdownItems($dropdown);
        const idx = $items.index(el);
        if (idx >= 0 && idx < $items.length - 1) {
            focusAt($items, idx + 1);
        }
    }
};

/**
 * Handle Up Arrow / Left Arrow navigation.
 *
 * - Top-level item: move to previous top-level item.
 * - Inside dropdown: move to previous item in the dropdown.
 */
const handleArrowBackward = (el: HTMLElement, $menu: JQuery): void => {
    if (isTopLevel(el)) {
        const $topItems = getTopLevelItems($menu);
        const idx = $topItems.index(el);
        if (idx > 0) {
            focusAt($topItems, idx - 1);
        }
        return;
    }

    if (isInDropdown(el)) {
        const $dropdown = $(el).closest('ul.dropdown-menu');
        const $items = getDropdownItems($dropdown);
        const idx = $items.index(el);
        if (idx > 0) {
            focusAt($items, idx - 1);
        }
    }
};

/**
 * Handle Home key — move focus to the first item in the current context.
 */
const handleHome = (el: HTMLElement, $menu: JQuery): void => {
    if (isTopLevel(el)) {
        const $topItems = getTopLevelItems($menu);
        if ($topItems.length) {
            focusAt($topItems, 0);
        }
        return;
    }

    if (isInDropdown(el)) {
        const $dropdown = $(el).closest('ul.dropdown-menu');
        const $items = getDropdownItems($dropdown);
        if ($items.length) {
            focusAt($items, 0);
        }
    }
};

/**
 * Handle End key — move focus to the last item in the current context.
 */
const handleEnd = (el: HTMLElement, $menu: JQuery): void => {
    if (isTopLevel(el)) {
        const $topItems = getTopLevelItems($menu);
        if ($topItems.length) {
            focusAt($topItems, $topItems.length - 1);
        }
        return;
    }

    if (isInDropdown(el)) {
        const $dropdown = $(el).closest('ul.dropdown-menu');
        const $items = getDropdownItems($dropdown);
        if ($items.length) {
            focusAt($items, $items.length - 1);
        }
    }
};

/**
 * Close the given open dropdown and focus its disclosure button.
 *
 * Focus must move to the disclosure button BEFORE calling menuHide because
 * SmartMenu.ts's hide protection (bindSmartMenusHideProtection) blocks hiding
 * when focus is still inside the submenu.
 *
 * For showcase menus, menuHide alone is insufficient because CSS !important
 * rules keyed off the 'highlighted' class override inline display styles.
 * The class is removed and ARIA state is reset manually.
 *
 * Returns true when the dropdown was actually closed (a button was found and
 * the close sequence ran). Returns false when no controlling button exists,
 * so callers can accurately compose fallback logic.
 */
const closeDropdown = ($dropdown: JQuery): boolean => {
    const $button = findButtonForSubmenu($dropdown);
    if (!$button.length) {
        return false;
    }

    // Move focus first so hide protection allows the close
    focusAt($button, 0);
    $button.attr('aria-expanded', 'false');

    const smartmenus = getSmartMenusInstance($button);
    if (smartmenus) {
        smartmenus.menuHide($dropdown);
    }

    // Showcase menus use CSS !important with a 'highlighted' class that
    // overrides SmartMenus' hide. Remove it and reset ARIA state.
    //
    // The showcase wrapper itself is role="presentation"; per ARIA 1.2 only
    // aria-hidden may be applied without invalidating the role, so we set
    // aria-hidden here and let the disclosure button carry aria-expanded.
    if (isShowcaseWrapper($dropdown)) {
        const $link = $button.siblings('a').first();
        if ($link.length) {
            $link.removeClass('highlighted');
        }
        $dropdown.attr('aria-hidden', 'true');
        $dropdown.find('ul[aria-hidden]').attr({
            'aria-hidden': 'true',
            'aria-expanded': 'false',
        });

        // WCAG 2.2 SC 1.4.13 — Dismissable: when the pointer is still over
        // the parent .top-nav, the `.top-nav:hover > .dropdown-menu` SCSS
        // rule keeps the menu visible despite `.highlighted` being removed.
        // Adding `ss-showcase-dismissed` to the .top-nav activates an
        // override rule (`display: none !important`) that beats :hover.
        // Cleared by clearShowcaseDismissClass on any re-show path
        // (see SmartMenu.ts activateShowcaseMenu / resetShowcaseMenuState
        // and SmartMenusSync.ts toggleMenuViaButton).
        $dropdown.closest('li.top-nav').addClass(SHOWCASE_DISMISSED_CLASS);
    }

    return true;
};

/**
 * Handle Escape key — close the open dropdown and focus its disclosure button.
 *
 * Overrides SmartMenus' default Escape behavior (which focuses the `<a>` link).
 *
 * Returns true when a dropdown was found AND successfully closed (i.e.
 * closeDropdown found a controlling button and ran). Returns false when there
 * is no enclosing dropdown, or the dropdown had no button, so the caller can
 * fall through to the next close strategy.
 */
const handleEscape = (el: HTMLElement): boolean => {
    const $dropdown = $(el).closest('ul.dropdown-menu');
    if (!$dropdown.length) {
        return false;
    }
    return closeDropdown($dropdown);
};

/**
 * Find the first open dropdown within $menu (in DOM order), identified by its
 * associated disclosure button having aria-expanded="true". Used as a fallback
 * for Escape when keyboard focus is not inside the open submenu (e.g. a
 * top-nav submenu opened via mouse hover).
 *
 * At most one disclosure button has aria-expanded="true" at any given moment,
 * so "first in DOM order" is equivalent to "the open one." We walk in DOM order
 * and return on the first hit.
 *
 * WCAG 2.2 SC 1.4.13 (Content on Hover or Focus) requires that hover-revealed
 * content be dismissable without moving pointer focus.
 */
const findOpenDropdownInMenu = ($menu: JQuery): JQuery => {
    let $open: JQuery = $();
    $menu.find('ul.dropdown-menu').each(function () {
        const $dd = $(this);
        const $button = findButtonForSubmenu($dd);
        if ($button.length && $button.attr('aria-expanded') === 'true') {
            $open = $dd;
            return false;
        }
    });
    return $open;
};

/**
 * Close the topmost open dropdown within $menu, regardless of where focus
 * currently is. Returns true when a dropdown was closed.
 */
const closeOpenDropdownInMenu = ($menu: JQuery): boolean => {
    const $dropdown = findOpenDropdownInMenu($menu);
    if (!$dropdown.length) {
        return false;
    }
    closeDropdown($dropdown);
    return true;
};

// =============================================================================
// Public Setup
// =============================================================================

/**
 * Set up arrow key, Home, End, and Escape navigation for all menu roots.
 *
 * Uses a capture-phase native event listener so it fires before SmartMenus.
 * Escape also calls stopPropagation() to prevent SmartMenus' rootKeyDown
 * from re-handling it.
 *
 * Additionally registers a document-level capture-phase Escape listener that
 * dismisses a hover-opened submenu when keyboard focus is outside the menu
 * root entirely (WCAG 2.2 SC 1.4.13).
 */
export const setupArrowKeyNavigation = (menuSelectors: readonly string[]): void => {
    $ = getJQuery();

    const menus: { el: HTMLElement; $menu: JQuery }[] = [];

    menuSelectors.forEach((selector) => {
        const menuEl = document.querySelector<HTMLElement>(selector);
        if (!menuEl) {
            return;
        }

        const $menu = $(menuEl);
        menus.push({ el: menuEl, $menu });

        menuEl.addEventListener(
            'keydown',
            (e: KeyboardEvent) => {
                if (!ARROW_NAV_KEYS.has(e.key)) {
                    return;
                }

                const el = e.target as HTMLElement;
                const tag = el.tagName.toLowerCase();

                // Escape: handle even when target is not an <a>/<button> inside a
                // dropdown. This covers focus on a disclosure button (sibling of
                // its controlled <ul>) and focus on the menu root after a hover.
                if (e.key === 'Escape') {
                    // First branch: focus is inside a dropdown — handleEscape walks
                    // up to the enclosing <ul> and closes it. Second branch: focus is
                    // on a sibling disclosure button or the menu root (the button is a
                    // sibling of, not a descendant of, its controlled <ul>, so the
                    // closest('ul.dropdown-menu') in handleEscape returns nothing) —
                    // closeOpenDropdownInMenu scans for the expanded dropdown and
                    // closes it regardless of focus position.
                    const closed = handleEscape(el) || closeOpenDropdownInMenu($menu);
                    if (closed) {
                        e.preventDefault();
                        e.stopPropagation();
                    }
                    return;
                }

                if (tag !== 'a' && tag !== 'button') {
                    return;
                }

                e.preventDefault();

                switch (e.key) {
                    case 'ArrowDown':
                    case 'ArrowRight':
                        handleArrowForward(el, $menu);
                        break;
                    case 'ArrowUp':
                    case 'ArrowLeft':
                        handleArrowBackward(el, $menu);
                        break;
                    case 'Home':
                        handleHome(el, $menu);
                        break;
                    case 'End':
                        handleEnd(el, $menu);
                        break;
                }
            },
            true,
        );
    });

    if (menus.length === 0) {
        return;
    }

    if (documentEscapeBound) {
        return;
    }
    documentEscapeBound = true;

    // Document-level fallback for Escape when focus is outside every menu root
    // (e.g. body focus after the user opens a top-nav submenu by mouse hover).
    // The per-menu listener above never sees these events because they target
    // an element outside menuEl, so the close must be initiated at the document.
    document.addEventListener(
        'keydown',
        (e: KeyboardEvent) => {
            if (e.key !== 'Escape') {
                return;
            }
            const target = e.target as Node | null;
            if (target && menus.some(({ el }) => el.contains(target))) {
                return;
            }
            for (const { $menu } of menus) {
                if (closeOpenDropdownInMenu($menu)) {
                    e.preventDefault();
                    e.stopPropagation();
                    return;
                }
            }
        },
        true,
    );
};
